import { Camera, RefreshCw } from 'lucide-react';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { useOptimizedPhotoOperations } from '../../hooks/useOptimizedPhotoOperations';
import photoService from '../../services/photoService';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  bulkDeletePhotos,
  clearSelection,
  fetchDropdownData,
  fetchPermissions,
  fetchPhotos,
  fetchPhotoStats,
  selectAllPhotos,
  setPage,
  setShowUploadModal,
  togglePhotoSelection,
} from '../../store/slices/photosSlice';
import { PhotoCategory, ProjectPhoto } from '../../types';
import { formatFileSize } from '../../utils/photoOptimizations';
import Pagination from '../Pagination';
import { PhotoBulkActions } from './PhotoBulkActions';
import { PhotoDetailsDrawer } from './PhotoDetailsDrawer';
import { PhotoFiltersHookForm } from './PhotoFiltersHookForm';
import { PhotoGallery } from './PhotoGallery';
import { PhotoUploadDrawerHookForm } from './PhotoUploadDrawerHookForm';

interface PhotoContainerReduxProps {
  isProjectSpecific?: boolean;
  projectId?: string;
}

export const PhotoContainerRedux: React.FC<PhotoContainerReduxProps> = memo(
  ({ isProjectSpecific = false, projectId }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const toast = useToast();

    const {
      photos,
      stats,
      permissions,
      loading,
      filterLoading,
      selectedPhotos,
      viewMode,
      showUploadModal,
      totalPhotos,
      currentPage,
      totalPages,
      query,
      error,
    } = useAppSelector(state => state.photos);

    const [showPhotoModal, setShowPhotoModal] = useState<ProjectPhoto | null>(null);

    // Initialize data
    useEffect(() => {
      const initializeData = async () => {
        await Promise.all([dispatch(fetchDropdownData()), dispatch(fetchPermissions(projectId))]);

        const initialQuery = {
          search: '',
          category: undefined,
          project_id: isProjectSpecific ? projectId : '',
          location: '',
          uploaded_by: '',
          sort_by: 'uploaded_at' as const,
          sort_order: 'desc' as const,
          date_from: '',
          date_to: '',
          page: 1,
          limit: 20,
        };

        await Promise.all([
          dispatch(fetchPhotos({ query: initialQuery, projectId })),
          dispatch(fetchPhotoStats(projectId)),
        ]);
      };

      initializeData();
    }, [dispatch, isProjectSpecific, projectId]);

    useEffect(() => {
      if (error) {
        toast.error(error);
      }
    }, [error, toast]);

    const {
      getCategoryInfo,
      formatDate,
      handleDelete,
      handleDownload,
      handleVersionHistory,
      handleAnnotationToggle,
    } = useOptimizedPhotoOperations(projectId);

    const handlePhotoClick = useCallback((photo: ProjectPhoto) => {
      setShowPhotoModal(photo);
    }, []);

    const handleBulkDownload = useCallback(async () => {
      if (selectedPhotos.length === 0) return;
      try {
        await photoService.bulkDownload(selectedPhotos, 'photos.zip', projectId);
        toast.success(t('photos.toast.bulkDownloadSuccess'));
      } catch {
        toast.error(t('photos.toast.bulkDownloadFailed'));
      }
    }, [selectedPhotos, projectId, toast, t]);

    const handleBulkDelete = useCallback(async () => {
      if (selectedPhotos.length === 0) return;
      if (
        !window.confirm(t('photos.confirmations.deletePhotos', { count: selectedPhotos.length }))
      ) {
        return;
      }
      try {
        const result = await dispatch(
          bulkDeletePhotos({ photoIds: selectedPhotos, projectId })
        ).unwrap();
        if (result.success) {
          await Promise.all([
            dispatch(fetchPhotos({ query, projectId })),
            dispatch(fetchPhotoStats(projectId)),
          ]);
          dispatch(clearSelection());
          toast.success(t('photos.toast.bulkDeleteSuccess'));
        }
      } catch {
        toast.error(t('photos.toast.bulkDeleteFailedRetry'));
      }
    }, [selectedPhotos, dispatch, projectId, query, toast, t]);

    const handleSelectionToggle = useCallback(
      (photoId: string) => dispatch(togglePhotoSelection(photoId)),
      [dispatch]
    );

    const handleSelectAll = useCallback(() => dispatch(selectAllPhotos()), [dispatch]);
    const handleClearSelection = useCallback(() => dispatch(clearSelection()), [dispatch]);

    const handlePageChange = useCallback(
      (page: number) => {
        dispatch(setPage(page));
        dispatch(fetchPhotos({ query: { ...query, page }, projectId }));
      },
      [dispatch, query, projectId]
    );

    const handleUploadClick = useCallback(() => dispatch(setShowUploadModal(true)), [dispatch]);
    const handleCloseUploadModal = useCallback(
      () => dispatch(setShowUploadModal(false)),
      [dispatch]
    );

    const handleUploadSuccess = useCallback(async () => {
      await Promise.all([
        dispatch(fetchPhotos({ query, projectId })),
        dispatch(fetchPhotoStats(projectId)),
      ]);
    }, [dispatch, query, projectId]);

    const handleRefresh = useCallback(() => {
      dispatch(fetchPhotos({ query, projectId }));
      dispatch(fetchPhotoStats(projectId));
    }, [dispatch, query, projectId]);

    if (loading && photos.length === 0) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      );
    }

    return (
      <div className="absolute inset-0 flex flex-col">
        {/* Compact Header Bar */}
        <div className="flex-none bg-white border-b border-gray-200">
          <div className="px-5 py-2.5">
            <div className="flex items-center justify-between">
              {/* Left: Title + Inline Stats */}
              <div className="flex items-center space-x-5">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                    <Camera className="h-4 w-4 text-brand-600" />
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-gray-900 leading-tight">
                      {isProjectSpecific ? t('photos.title') : t('photos.allPhotos')}
                    </h1>
                    <p className="text-[11px] text-gray-500 leading-tight">
                      {isProjectSpecific ? t('photos.subtitle.project') : t('photos.subtitle.all')}
                    </p>
                  </div>
                </div>

                {/* Inline Stats - Compact */}
                {stats && (
                  <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-gray-900">
                        {stats.total_photos.toLocaleString()}
                      </span>
                      <span className="text-[10px] text-gray-400">{t('photos.stats.total')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-green-600">
                        {stats.recent_uploads.toLocaleString()}
                      </span>
                      <span className="text-[10px] text-gray-400">{t('photos.stats.recent')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-purple-600">
                        {formatFileSize(stats.storage_used)}
                      </span>
                      <span className="text-[10px] text-gray-400">{t('photos.stats.storage')}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                  title={t('common.refresh')}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 p-4 bg-gray-50 overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            {/* Filters & Actions */}
            <div className="flex-none">
              <PhotoFiltersHookForm
                isProjectSpecific={isProjectSpecific}
                projectId={projectId}
                totalPhotos={totalPhotos}
                selectedPhotosCount={selectedPhotos.length}
                onUploadClick={handleUploadClick}
              />
            </div>

            {/* Bulk Actions Bar */}
            {selectedPhotos.length > 0 && (
              <div className="flex-none">
                <PhotoBulkActions
                  selectedCount={selectedPhotos.length}
                  onBulkDownload={handleBulkDownload}
                  onBulkDelete={handleBulkDelete}
                  onClearSelection={handleClearSelection}
                  permissions={permissions}
                />
              </div>
            )}

            {/* Photo Gallery */}
            <div className="flex-1 min-h-0 overflow-auto">
              <PhotoGallery
                photos={photos}
                selectedPhotos={selectedPhotos}
                permissions={permissions}
                viewMode={viewMode}
                filterLoading={filterLoading}
                searchTerm={query.search || ''}
                selectedCategory={query.category || ''}
                selectedProject={query.project_id || ''}
                onPhotoClick={handlePhotoClick}
                onSelectionToggle={handleSelectionToggle}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                onDownload={handleDownload}
                onDelete={handleDelete}
                getCategoryInfo={getCategoryInfo}
                formatDate={formatDate}
                onUploadClick={handleUploadClick}
              />
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex-none border-t border-gray-100">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalPhotos}
                  itemsPerPage={query.limit || 20}
                  onPageChange={handlePageChange}
                  className="p-3"
                />
              </div>
            )}
          </div>
        </div>

        {/* Upload Drawer */}
        <PhotoUploadDrawerHookForm
          isOpen={showUploadModal}
          onClose={handleCloseUploadModal}
          onUploadSuccess={handleUploadSuccess}
          isProjectSpecific={isProjectSpecific}
        />

        {/* Photo Details Drawer */}
        <PhotoDetailsDrawer
          photo={showPhotoModal}
          isOpen={!!showPhotoModal}
          onClose={() => setShowPhotoModal(null)}
          onDownload={handleDownload}
          onDelete={handleDelete}
          onVersionHistory={handleVersionHistory}
          onAnnotationToggle={() => handleAnnotationToggle(false)}
          getCategoryInfo={(category: string) => getCategoryInfo(category as PhotoCategory)}
          formatDate={formatDate}
          permissions={{
            download: permissions.download ?? true,
            delete: permissions.delete ?? true,
            edit: permissions.edit ?? true,
            view_history: permissions.view_history ?? false,
            annotate: permissions.annotate ?? false,
          }}
          showAnnotationMode={false}
        />
      </div>
    );
  }
);

PhotoContainerRedux.displayName = 'PhotoContainerRedux';
